<template>
  <BaseModalLarge
    :heading="$t(`global.items.${resourceType}`, 1)"
    :loading="$apollo.loading"
    :testId="resourceType"
    :parentRoute="parentRouteName"
    useForm
    @submit="submit"
    @close="$emit('close')"
  >
    <div
      :class="[$style.base, {[$style.smallScreen]: $screen === 's', [$style.largeScreen]: $screen === 'l'}]"
    >
      <ResourcePrice
        v-if="showPriceInfo"
        :type="resourceType"
        learnMoreLink
        small
        :mb="1"
      />
      <div :class="$style.top">
        <BaseCard :heading="$t('admin.resources.basic_information')">
          <Flex direction="column">
            <BaseInput
              v-model="formData.name"
              :label="$t('global.name')"
              required
              :maxLength="255"
              v-test="'resource-name'"
            />
            <Flex
              v-if="isEmployee"
              direction="column"
            >
              <div :class="$style.row">
                <BaseDropdown
                  v-model="formData.gender"
                  :options="genderOptions"
                  :label="$t('global.gender')"
                  v-test="'resource-gender'"
                />
                <BaseInput
                  v-model="formData.phone"
                  :label="$t('global.phone_number')"
                  type="tel"
                  v-test="'resource-phone'"
                />
              </div>
              <BaseInput
                v-model="formData.email"
                :label="$t('global.email_address')"
                type="email"
                v-test="'resource-email'"
              />
              <div
                v-if="company.medical"
                :class="$style.row"
              >
                <BaseInput
                  v-model="formData.healthcareCode"
                  :label="$t('resource.healthcare_code')"
                  :info="$t('resource.healthcare_code_info')"
                  v-test="'resource-healthcare-code'"
                />
                <BaseInput
                  v-model="formData.jobRole"
                  :label="$t('resource.job_role')"
                  v-test="'resource-job-role'"
                />
              </div>
            </Flex>
            <div v-if="showTypeSelect">
              <BaseRadio
                v-model="formData.type"
                :options="typeOptions"
                :label="$t('global.type')"
                inline
                v-test="'resource-type'"
              />
            </div>
          </Flex>
        </BaseCard>
        <BaseCard :heading="$t('admin.resources.visual_appearance')">
          <Flex direction="column">
            <AvatarWithUploader
              :resource="formData"
              @setPicture="setPicture"
              v-test="'resource-avatar'"
            />
            <ColorSelection
              v-model:colorSetting="colorSetting"
              v-model:color="formData.color"
              :modelType="resourceType"
              v-test="'resource-color'"
            />
          </Flex>
        </BaseCard>
      </div>
      <BaseCard :heading="$t('admin.resources.online_bookings')">
        <BaseCheckbox
          v-model="formData.bookable"
          :label="$t(`${resourceType}.allow_online_booking`)"
          v-test="'resource-bookable'"
        />
      </BaseCard>
      <RosterOptions
        v-model:customRoster="formData.customRoster"
        v-model:rosterLocation="formData.rosterLocationId"
        :resourceType="resourceType"
        :multiLocation="multiLocation"
      />
      <Services
        v-model="formData.serviceIds"
        :resourceType="resourceType"
        :disableDeselect="disableDeselectServices"
        v-test="'resource-services'"
      />
      <BaseCard
        v-if="multiLocation"
        :heading="$t('global.items.location', 2)"
      >
        <Locations
          v-model="formData.locationIds"
          gray
          v-test="'resource-locations-list'"
        />
        <BaseText
          v-if="showLocationIdsError"
          color="error"
          :mt="0.5"
          v-test="'resource-locations-error'"
        >
          *{{ $t('resource.location_error') }}
        </BaseText>
      </BaseCard>
      <BaseCard
        v-if="isEmployee && unleash.isEnabled('EmployeeProfile')"
        :heading="$t('admin.resources.online_profile')"
      >
        <Flex direction="column">
          <Profile
            v-model:enabled="formData.showOnlineProfile"
            v-model:description="formData.description"
            v-model:languages="formData.languages"
            v-model:jobTitle="formData.jobTitle"
            v-model:portfolio="formData.portfolio"
          />
        </Flex>
      </BaseCard>
    </div>

    <template #footer>
      <Footer
        :resource="resource"
        :isLoading="isLoading"
        @close="close"
      />
    </template>
  </BaseModalLarge>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapActions, mapState } from 'pinia';
import { defineComponent } from 'vue';
import { resourceFragment } from '@/graphql-fragments';
import { uniqueArray } from '@/helpers/formatting';
import { flash } from '@/helpers/ui';
import ColorSelection from '@/modules/admin/_shared/ColorSelection.vue';
import Locations from '@/modules/admin/_shared/LocationSelect.vue';
import ResourcePrice from '@/modules/admin/resources/ResourcePrice.vue';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useResourcesStore } from '@/stores/resources';
import { useServicesStore } from '@/stores/services';
import unleash from '@/unleash';
import AvatarWithUploader from './AvatarWithUploader.vue';
import Footer from './Footer.vue';
import Profile from './profile/index.vue';
import RosterOptions from './RosterOptions.vue';
import Services from './services/index.vue';

export default defineComponent({
  name: 'ResourceForm',
  components: {
    AvatarWithUploader,
    ColorSelection,
    Footer,
    Services,
    Locations,
    ResourcePrice,
    RosterOptions,
    Profile,
  },
  props: {
    type: {
      type: String,
      validator: (value) =>
        !value || ['employee', 'room', 'equipment'].includes(value),
    },
    parentRouteName: String,
  },
  emits: ['created', 'updated', 'close'],
  setup() {
    const { createResource, updateResource } = useResourcesStore();
    return {
      createResource,
      updateResource,
      unleash,
    };
  },
  data() {
    return {
      isLoading: false,
      colorSetting: null,
      showLocationIdsError: false,
      formData: {
        name: null,
        gender: null,
        phone: null,
        email: null,
        color: null,
        bookable: true,
        customRoster: false,
        picture: null,
        type: 'room',
        locationIds: [],
        rosterLocationId: null,
        serviceIds: [],
        healthcareCode: null,
        jobRole: null,
        description: null,
        languages: [],
        jobTitle: null,
        portfolio: [],
        showOnlineProfile: false,
      },
      genderOptions: [
        { value: null, label: this.$t('global.unknown') },
        { value: 'female', label: this.$t('global.female') },
        { value: 'male', label: this.$t('global.male') },
      ],
      typeOptions: [
        { value: 'room', label: this.$t('global.items.room', 1) },
        { value: 'equipment', label: this.$t('global.items.equipment', 1) },
      ],
    };
  },
  apollo: {
    resource: {
      query: gql`
        query getResource($id: ID!) {
          resource(id: $id) {
            ...resource
            serviceIds
            locationIds
            customRoster
            rosterLocationId
            description
            jobTitle
            languages {
              code
              proficiency
            }
            portfolio
            showOnlineProfile
          }
        }
        ${resourceFragment}
      `,
      variables() {
        return {
          id: this.resourceId,
        };
      },
      skip() {
        return !this.resourceId;
      },
      result() {
        Object.keys(this.formData).forEach((key) => {
          if (key === 'languages') {
            this.formData[key] = this.resource.languages.map((language) => ({
              code: language.code,
              proficiency: language.proficiency,
            }));
          } else {
            this.formData[key] = this.resource[key];
          }
        });
      },
    },
  },
  watch: {
    type: {
      handler(value) {
        if (value) {
          this.formData.type = value;
        }
      },
      immediate: true,
    },
    'formData.rosterLocationId': {
      handler(value) {
        if (
          !this.formData.customRoster &&
          !this.formData.locationIds.includes(value)
        ) {
          this.formData.locationIds.push(value);
        }
      },
    },
    'formData.customRoster': {
      handler(value) {
        if (
          !value &&
          !this.formData.locationIds.includes(this.formData.rosterLocationId)
        ) {
          this.formData.locationIds.push(this.formData.rosterLocationId);
        }
      },
    },
    services: {
      handler(value) {
        if (value && !this.resourceId && this.isEmployee) {
          this.formData.serviceIds = value.map((service) => service.id);
        }
      },
      immediate: true,
      deep: true,
    },
    locations: {
      handler(value) {
        if (value && !this.resourceId) {
          this.formData.locationIds = value.map((location) => location.id);
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(useCompanyStore, [
      'company',
      'companySettings',
      'multiLocation',
      'isMarketLaunchCountry',
    ]),
    ...mapState(useResourcesStore, ['hasSingleEmployee']),
    ...mapState(useLocationsStore, ['locationId', 'locations']),
    ...mapState(useServicesStore, ['services']),
    resourceId() {
      return this.$route.params.id;
    },
    isEmployee() {
      return this.type === 'employee';
    },
    showTypeSelect() {
      return !this.isEmployee && !this.resourceId;
    },
    showPriceInfo() {
      return (
        !this.company.freeEmployees &&
        !this.resource &&
        this.isEmployee &&
        !this.isMarketLaunchCountry
      );
    },
    resourceType() {
      return this.isEmployee ? 'employee' : 'resource';
    },
    colorSettingChanged() {
      return (
        this.colorSetting &&
        this.colorSetting !== this.companySettings.agenda.colour
      );
    },
    disableDeselectServices() {
      // The user won't be able to deselect services when editing rooms and equipments
      return !this.isEmployee && !!this.resourceId;
    },
  },
  methods: {
    ...mapActions(useCompanyStore, ['updateCompany']),
    close() {
      if (this.parentRouteName) {
        this.$router.push({ name: this.parentRouteName });
      } else {
        this.$emit('close');
      }
    },
    setPicture(picture) {
      this.formData.picture = picture?.public_id || '';
    },
    submit() {
      if (this.multiLocation && !this.formData.locationIds.length) {
        this.showLocationIdsError = true;
        return;
      }

      this.isLoading = true;
      const input = { ...this.formData };
      input.type = this.formData.type?.toUpperCase() || 'EMPLOYEE';
      input.rosterLocationId = input.customRoster
        ? null
        : this.formData.rosterLocationId || this.locationId;

      if (!this.isEmployee) {
        input.gender = undefined;
      }

      if (input.languages?.length) {
        input.languages = uniqueArray(input.languages, 'code');
      }

      if (this.resource) {
        input.id = this.resource.id;
        this.updateResource(input)
          .then(() => {
            this.updateColorSetting();
            flash(this.$t('global.flash.resource_updated'));
            this.$emit('updated', this.resource.id);
            this.close();
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.createResource(input)
          .then((resourceId) => {
            this.updateColorSetting();
            flash(this.$t('global.flash.resource_created'));
            this.$emit('created', resourceId);
            this.close();
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    updateColorSetting() {
      if (this.colorSettingChanged) {
        this.updateCompany({
          settings: {
            agenda: {
              colour: this.colorSetting,
            },
          },
        });
      }
    },
  },
  created() {
    if (!this.resourceId && this.isEmployee) {
      this.formData.customRoster = true;
    }
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.top {
  display: flex;
  gap: $spacing;

  .base:not(.smallScreen) & {
    & > *:first-child {
      flex: 2;
    }

    & > *:last-child {
      flex: 1;
    }
  }

  .base.smallScreen & {
    flex-direction: column;
  }
}

.row {
  display: flex;
  gap: $spacing;

  .base.largeScreen & {
    & > * {
      flex: 1;
    }
  }

  .base:not(.largeScreen) & {
    flex-direction: column;
  }
}
</style>
